import { ScrollerType } from 'app/data/Enums';
import { IntersectBehaviour } from '../behaviours/scroll/IntersectBehaviour';
import { RegionBehaviour } from '../behaviours/scroll/RegionBehaviour';

export enum BehaviourType {
	INTERSECT = 'intersect',
	REGION = 'region'
}

export interface Behaviourable {
	resize: Function;
	destroy: Function;
	update: Function;
}

export interface BehaviourConfig {
	scrollerType: ScrollerType;
	type: BehaviourType;
	id: string;
	hideOutsideViewport?: boolean;
	selector?: string;
	action?: string;
	data?: string;
}

/** @class Mananger to manage scroll behaviours */
export class ScrollBehaviourManager {
	private _behaviours: Array<Behaviourable> = [];
	private _scroll: number = 0;
	private _container: HTMLElement;
	private _scrollerType: ScrollerType;

	public setType(type: ScrollerType): void {
		this._scrollerType = type;
	}

	/**
	 * Update the scroll
	 * @param {number} scroll - Y position of the scroll
	 */
	public updateScroll(scroll: number): void {
		this._scroll = scroll;
		this._updateBehaviours(this._scroll);
	}

	/**
	 * Set target container
	 * @param {HTMLElement} container - The container with the elements to apply behaviours to
	 */
	public setContainer(element: HTMLElement): void {
		this._destroyBehaviours();
		this._removeListeners();
		this._container = element;

		if (this._container) {
			this._createBehaviours();
			this._container.addEventListener('resize', this.resize.bind(this));
		}
	}

	/** Resize event */
	public resize(): void {
		this._resizeBehaviours();
	}

	//Private
	//------------------------------------------------------------------------------

	/** Destroy all 'scroll' behaviours from the old container */
	private _destroyBehaviours(): void {
		this._behaviours.forEach((behaviour) => {
			behaviour.destroy();
		});
		this._behaviours = [];
	}

	/** Remove listeners */
	private _removeListeners(): void {
		if (this._container) {
			this._container.removeEventListener('resize', this.resize.bind(this));
		}
	}

	/** Create all 'scroll' behaviours found in the container */
	private _createBehaviours(): void {
		this._container.querySelectorAll('.behaviour').forEach((element: HTMLElement) => {
			let id: string = element.getAttribute('id');
			let behaviours: Array<BehaviourConfig> = JSON.parse(element.getAttribute('data-behaviour')).behaviours;
			if (this._checkValidId(id, behaviours)) {
				this._createElementBehaviours(behaviours, id);
			}
		});
	}

	/** Create all the behaviours in one element
	 * @param {Array<BehaviourConfig>} configList - Array of config value
	 */
	private _createElementBehaviours(configList: Array<BehaviourConfig>, id: string): void {
		configList.forEach((config: BehaviourConfig) => {
			//Set the id from the 'id' atrr on the tag:
			config.id = id;
			config.hideOutsideViewport = true;
			config.scrollerType = this._scrollerType;

			if (config.type === BehaviourType.INTERSECT) {
				this._behaviours.push(new IntersectBehaviour(config, this._container));
			} else if (config.type === BehaviourType.REGION) {
				this._behaviours.push(new RegionBehaviour(config, this._container));
			}
		});
	}

	/** Resize all 'scroll' behaviours from the old container */
	private _resizeBehaviours(): void {
		this._behaviours.forEach((behaviour) => {
			behaviour.resize();
		});

		(window as any).observer.observe();
	}

	/** Update all the bahviours on scroll
	 * @param {number} scroll
	 */
	private _updateBehaviours(scroll: number): void {
		this._behaviours.forEach((behaviour) => {
			behaviour.update(scroll);
		});
	}

	/** Check if the ID is set
	 * @param {string} id
	 * @param {Array<BehaviourConfig>} config
	 * @throws {Error}
	 * @returns {boolean}
	 */
	private _checkValidId(id: string, config: Array<BehaviourConfig>): boolean {
		if (!id || id === '') {
			try {
				throw new Error(`Behaviour ID has not been set on region/element : ${JSON.stringify(config)}`);
			} catch (e) {
				console.log(e);
			}
			return false;
		}

		return true;
	}
}
